import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Plus, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { createPost, updatePost, getUserPosts, deletePost } from '../services/api';
import { Post, CreatePostFormData, UpdatePostFormData } from '../types';
import { ROUTES } from '../constants';
import { Button } from '../components/Button';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Header } from '../components/Header';
import CreatePostModal from '../components/CreatePostModal';
import EditPostModal from '../components/EditPostModal';
import ConfirmDeletePostModal from '../components/ConfirmDeletePostModal';
import PostCard from '../components/PostCard';

const MyPostsPage: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }

  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate(ROUTES.LOGIN);
      return;
    }

    if (user.role !== 'user') {
      navigate(ROUTES.HOME_PAGE);
      return;
    }

    fetchPosts();
  }, [user, loading, navigate]);

  const fetchPosts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getUserPosts();
      if (response.success) {
        setPosts(response.posts);
      } else {
        setError(response.error || 'Failed to load posts');
      }
    } catch (err) {
      setError('An error occurred while loading posts');
      console.error('Error fetching posts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePost = async (formData: CreatePostFormData) => {
    try {
      const response = await createPost(formData);
      if (response.success) {
        setSuccessMessage('Post created successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        await fetchPosts();
        setIsCreateModalOpen(false);
      } else {
        alert(response.message || 'Failed to create post');
      }
    } catch (err) {
      console.error('Error creating post:', err);
      alert('An error occurred while creating the post');
    }
  };

  const handleUpdatePost = async (formData: UpdatePostFormData) => {
    try {
      const response = await updatePost(formData);
      if (response.success) {
        setSuccessMessage('Post updated successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        await fetchPosts();
        setIsEditModalOpen(false);
      } else {
        alert(response.message || 'Failed to update post');
      }
    } catch (err) {
      console.error('Error updating post:', err);
      alert('An error occurred while updating the post');
    }
  };

  const handleEditPost = (post: Post) => {
    setSelectedPost(post);
    setIsEditModalOpen(true);
  };

  const handleDeletePost = (post: Post) => {
    setSelectedPost(post);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPost) return;

    try {
      const response = await deletePost(selectedPost.postId);
      if (response.success) {
        setSuccessMessage('Post deleted successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        await fetchPosts();
        setIsDeleteModalOpen(false);
        setSelectedPost(null);
      } else {
        alert(response.message || 'Failed to delete post');
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('An error occurred while deleting the post');
    }
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header theme="blue" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Care Request Posts</h1>
              <p className="mt-2 text-gray-600">
                Create and manage your care request posts to connect with providers
              </p>
            </div>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create New Post
            </Button>
          </div>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
            <Button onClick={fetchPosts} variant="outline" className="mt-2">
              Retry
            </Button>
          </div>
        )}

        {posts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center">
                <FileText className="w-10 h-10 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">No posts yet</h3>
              <p className="text-gray-600 max-w-md">
                Create your first care request post to let providers know what services you need.
                They can then reach out to you with their offerings.
              </p>
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="mt-4 flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create Your First Post
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <PostCard
                key={post.postId}
                post={post}
                onEdit={handleEditPost}
                onDelete={handleDeletePost}
                viewMode="owner"
              />
            ))}
          </div>
        )}
      </div>

      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreatePost}
      />

      {selectedPost && (
        <>
          <EditPostModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedPost(null);
            }}
            onSubmit={handleUpdatePost}
            post={selectedPost}
          />

          <ConfirmDeletePostModal
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedPost(null);
            }}
            onConfirm={handleConfirmDelete}
            postTitle={selectedPost.title}
          />
        </>
      )}
    </div>
  );
};

export default MyPostsPage;
